import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from "react-leaflet";
import L from "leaflet";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Fix default icon URLs
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const PIN_COLORS = {
  potensi: "#F97316",
  sudah_kontak: "#3B82F6",
  exclusive: "#10B981",
  note: "#8B5CF6",
};
const PIN_LABELS = {
  potensi: "Potensi Jual",
  sudah_kontak: "Sudah Dikontak",
  exclusive: "Exclusive Mandate",
  note: "Catatan",
};

function makeIcon(color) {
  return L.divIcon({
    className: "",
    html: `<div class="radar-pin" style="background:${color}">
             <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 7-8 12-8 12s-8-5-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></svg>
           </div>`,
    iconSize: [32, 32], iconAnchor: [16, 32],
  });
}

function ClickHandler({ onPick }) {
  useMapEvents({ click(e) { onPick(e.latlng); } });
  return null;
}

export default function CanvassingMap() {
  const [pins, setPins] = useState([]);
  const [listings, setListings] = useState([]);
  const [picked, setPicked] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", pin_type: "potensi", notes: "" });

  const load = async () => {
    const [{ data: p }, { data: l }] = await Promise.all([
      api.get("/map-pins"), api.get("/listings?mine=true"),
    ]);
    setPins(p);
    setListings(l.filter((x) => x.lat && x.lng));
  };
  useEffect(() => { load(); }, []);

  const onPick = (latlng) => {
    setPicked(latlng);
    setForm({ label: "", pin_type: "potensi", notes: "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!picked) return;
    try {
      await api.post("/map-pins", {
        lat: picked.lat, lng: picked.lng,
        label: form.label || "Pin baru",
        pin_type: form.pin_type, notes: form.notes,
      });
      toast.success("Pin ditambahkan");
      setOpen(false); setPicked(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/map-pins/${id}`); load(); toast.success("Pin dihapus"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const center = pins[0] ? [pins[0].lat, pins[0].lng] : listings[0] ? [listings[0].lat, listings[0].lng] : [-6.2088, 106.8456];

  return (
    <div className="space-y-6" data-testid="map-page">
      <div>
        <span className="text-xs uppercase tracking-widest text-orange-400 font-semibold">Radar</span>
        <h1 className="text-3xl font-extrabold tracking-tight mt-1">Peta Canvassing</h1>
        <p className="text-slate-400 text-sm mt-1">Klik di peta untuk tandai lokasi. Pin otomatis tersimpan.</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 rounded-2xl overflow-hidden border border-white/10 bg-[#0D1322]" style={{ height: "70vh" }}>
          <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap contributors" />
            <ClickHandler onPick={onPick} />
            {pins.map((p) => (
              <Marker key={p.id} position={[p.lat, p.lng]} icon={makeIcon(PIN_COLORS[p.pin_type] || "#F97316")}>
                <Popup>
                  <div className="min-w-[200px]">
                    <div className="font-semibold">{p.label}</div>
                    <div className="text-xs text-slate-400 mt-1">{PIN_LABELS[p.pin_type]}</div>
                    {p.notes && <div className="text-xs mt-2 text-slate-300">{p.notes}</div>}
                    <button onClick={() => remove(p.id)} className="mt-3 text-xs text-red-400 hover:text-red-300 flex items-center gap-1" data-testid={`delete-pin-${p.id}`}>
                      <Trash2 className="w-3 h-3" /> Hapus pin
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
            {listings.map((l) => (
              <Marker key={l.id} position={[l.lat, l.lng]} icon={makeIcon("#2563EB")}>
                <Popup>
                  <div className="min-w-[200px]">
                    <div className="font-semibold">{l.title}</div>
                    <div className="text-xs text-slate-400 mt-1">{l.location}</div>
                    <a href={`/app/listings/${l.id}`} className="text-xs text-orange-400 mt-2 block">Lihat listing →</a>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <aside className="rounded-2xl bg-[#141C2E] border border-white/5 p-5 space-y-4" data-testid="legend-panel">
          <h3 className="font-semibold">Legenda</h3>
          <ul className="space-y-2 text-sm">
            {Object.entries(PIN_LABELS).map(([k, v]) => (
              <li key={k} className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full inline-block" style={{ background: PIN_COLORS[k] }}></span>
                <span className="text-slate-300">{v}</span>
              </li>
            ))}
            <li className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full inline-block" style={{ background: "#2563EB" }}></span>
              <span className="text-slate-300">Listing Anda</span>
            </li>
          </ul>
          <div className="pt-3 border-t border-white/5 text-xs text-slate-500">
            Total pins: <span className="text-slate-200 font-semibold">{pins.length}</span>
          </div>
        </aside>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141C2E] border-white/10 text-slate-100">
          <DialogHeader><DialogTitle>Tandai Lokasi</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="text-xs text-slate-500">
              <MapPin className="w-3 h-3 inline mr-1" />
              {picked?.lat?.toFixed(5)}, {picked?.lng?.toFixed(5)}
            </div>
            <div><Label>Label</Label><Input value={form.label} data-testid="pin-label" onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1 bg-[#0D1322] border-white/10" placeholder="Rumah pak Ahmad" /></div>
            <div>
              <Label>Tipe</Label>
              <Select value={form.pin_type} onValueChange={(v) => setForm({ ...form, pin_type: v })}>
                <SelectTrigger data-testid="pin-type" className="mt-1 bg-[#0D1322] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141C2E] border-white/10 text-slate-100">
                  <SelectItem value="potensi">Potensi Jual</SelectItem>
                  <SelectItem value="sudah_kontak">Sudah Dikontak</SelectItem>
                  <SelectItem value="exclusive">Exclusive Mandate</SelectItem>
                  <SelectItem value="note">Catatan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Catatan</Label><Textarea rows={3} value={form.notes} data-testid="pin-notes" onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0D1322] border-white/10 resize-none" /></div>
            <Button type="submit" data-testid="pin-submit" className="w-full bg-orange-500 hover:bg-orange-600 font-semibold">Simpan Pin</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
